import React from "react";
import { BreadcrumbsWrapper, Container, Alert, ConnectedAlert, ProgressBar } from "../../../components";
import { Text, Heading } from "@telia/teddy";
import _size from "lodash/size";
import { connect } from "react-redux";
import { RootState } from "../../../../../models";
import { EsimProduct } from "../../../../../models/state";
import { getErrorMessage } from "../../../app/state/selectors";
import { isBankIdVerified as isBankIdVerifiedSelector } from "../../../authentication/state/selectors";
import BankIdSection from "../../../checkout/components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import EsimCard from "../../components/atoms/EsimCard/EsimCard";
import {
    getEsimProducts,
    getOrderDescription,
    isB2B as getIsB2B,
    isPhoneNumberVerified as getIsPhoneNumberVerified,
    isLoggedInAsLegal as getIsLoggedInAsLegal,
    getAdminPhoneNumbers,
    hasProductUserMatch as getHasProductUserMatch,
} from "../../state/selectors";
import TEXT from "../../text-consts";
import PhoneNumberCheck from "../PhoneNumberCheck/PhoneNumeberCheck";
import "./InstallEsim.less";

interface StateProps {
    adminPhoneNumbers: string[];
    errorMessage?: string;
    esimProducts: EsimProduct[];
    hasProductUserMatch: boolean;
    isB2B: boolean;
    isBankIdVerified: boolean;
    isFetchingData: boolean;
    isLoggedInAsLegal: boolean;
    isPhoneNumberVerified: boolean;
    orderDescription: string;
}

type InstallEsimProps = StateProps;

const InstallEsim: React.FC<InstallEsimProps> = ({
    // Redux
    adminPhoneNumbers,
    hasProductUserMatch,
    isFetchingData,
    isBankIdVerified,
    esimProducts,
    orderDescription,
    isB2B,
    isPhoneNumberVerified,
    isLoggedInAsLegal,
    errorMessage,
}) => {
    const isInstallmentNeeded = orderDescription === "" || isPhoneNumberVerified;

    if (!isInstallmentNeeded && !(isB2B && !isPhoneNumberVerified && _size(adminPhoneNumbers) > 0)) {
        const heading = TEXT.ERRORS.HEADING;

        return (
            <div className="install-esim">
                <div className="install-esim__heading-container">
                    <div className="install-esim__heading-inner">
                        <BreadcrumbsWrapper
                            breadcrumbs={[
                                { name: "Forsiden", link: "/" },
                                { name: "Installere eSIM", link: "/esim/installering" },
                            ]}
                        />
                        <div className="install-esim__heading-wrapper">
                            <Heading mb="100" as="h1" variant="title-600" className="install-esim__heading">
                                {TEXT.HEADING}
                            </Heading>
                            <Text mb="100">{isB2B ? TEXT.SUB_HEADING_B2B : TEXT.SUB_HEADING}</Text>
                        </div>
                    </div>
                </div>
                <Container noPaddingTop size="medium">
                    <Heading mb="100" as="h2" variant="title-200">
                        {heading}
                    </Heading>
                    <Text mb="100">{orderDescription}</Text>
                    {errorMessage && <Alert kind="warning">{errorMessage}</Alert>}
                </Container>
            </div>
        );
    }

    const shouldShowProductsForB2BProductUser = isB2B && hasProductUserMatch;
    const shouldShowProductsForB2BLegalOwner = isB2B && (isPhoneNumberVerified || isLoggedInAsLegal); // either verified by pin OR orderer=bankid
    const shouldShowB2BProducts = shouldShowProductsForB2BLegalOwner || shouldShowProductsForB2BProductUser;

    return (
        <div className="install-esim">
            <div className="install-esim__heading-container">
                <div className="install-esim__heading-inner">
                    <BreadcrumbsWrapper
                        breadcrumbs={[
                            { name: "Forsiden", link: "/" },
                            { name: "Installere eSIM", link: "/esim/installering" },
                        ]}
                    />
                    <div className="install-esim__heading-wrapper">
                        <Heading mb="100" as="h1" variant="title-600" className="install-esim__heading">
                            {TEXT.HEADING}
                        </Heading>
                        <Text mb="100">{isB2B ? TEXT.SUB_HEADING_B2B : TEXT.SUB_HEADING}</Text>
                    </div>
                </div>
            </div>
            <ProgressBar isFetchingData={isFetchingData} standByColor="light-grey" />
            <ConnectedAlert />
            {isInstallmentNeeded && !isBankIdVerified && (
                <Container className="install-esim__bankid-container">
                    <BankIdSection referer="esim" typeId="esim_bankid" />
                </Container>
            )}
            {isBankIdVerified && (!isB2B || shouldShowB2BProducts) && (
                <Container className="install-esim__esim-list" noPaddingTop size="medium">
                    <Text mb="100">{TEXT.ESIM.ESIM_DESCRIPTION}</Text>
                    <Text mb="100">{TEXT.ESIM.ESIM_DESCRIPTION_NOTE}</Text>
                    <Heading mb="100" as="h2" variant="title-200">
                        {TEXT.ESIM.ESIM_HEADING}
                    </Heading>
                    {esimProducts.map((esim) => (
                        <EsimCard
                            key={esim.iccid}
                            iccid={esim.iccid}
                            type={esim.type}
                            phoneNumber={esim.phoneNumber || ""}
                            productUser={esim.productUser}
                            isActive={esim.isActive}
                            profileStatus={esim.profileStatus}
                            activationCode={esim.activationCode}
                        />
                    ))}
                </Container>
            )}
            {isBankIdVerified &&
                isB2B &&
                !isLoggedInAsLegal &&
                !isPhoneNumberVerified &&
                !hasProductUserMatch &&
                _size(adminPhoneNumbers) > 0 && <PhoneNumberCheck />}
            {isBankIdVerified &&
                isB2B &&
                !isPhoneNumberVerified &&
                _size(adminPhoneNumbers) < 1 &&
                !shouldShowB2BProducts && (
                    <div className="install-esim__nonadmins-wrapper">
                        <Text mb="100">{TEXT.NO_ADMIN_NUMBERS_INFO}</Text>
                    </div>
                )}
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    // Loading
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    // Authentication
    isBankIdVerified: isBankIdVerifiedSelector(state),
    // Esim info
    adminPhoneNumbers: getAdminPhoneNumbers(state),
    esimProducts: getEsimProducts(state),
    hasProductUserMatch: getHasProductUserMatch(state),
    orderDescription: getOrderDescription(state),
    isB2B: getIsB2B(state),
    isPhoneNumberVerified: getIsPhoneNumberVerified(state),
    isLoggedInAsLegal: getIsLoggedInAsLegal(state),
    errorMessage: getErrorMessage(state),
});

export default connect(mapStateToProps)(InstallEsim);
